import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { DcrAttachment } from '../../_types/attachment';

import { attachmentThumbnailStyles as sx } from './AttachmentThumbnail.styles';

type Props = {
	attachment: DcrAttachment;
	onRemove?: (id: string) => void;
	onClick?: (attachment: DcrAttachment) => void;
};

export const AttachmentThumbnail = ({
	attachment,
	onRemove,
	onClick
}: Props) => {
	const { t } = useTranslation();
	const isUploading = attachment.status === 'uploading';
	const hasError = attachment.status === 'error';
	const clickable = !!onClick && !isUploading && !hasError;

	const handleClick = (e: MouseEvent) => {
		e.stopPropagation();
		if (clickable) onClick?.(attachment);
	};

	const handleRemove = (e: MouseEvent) => {
		e.stopPropagation();
		onRemove?.(attachment.id);
	};

	const handleKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			e.stopPropagation();
			onClick?.(attachment);
		}
	};

	return (
		<Tooltip title={hasError ? attachment.error : attachment.name}>
			<Box
				sx={sx.root(clickable)}
				onClick={handleClick}
				role={clickable ? 'button' : undefined}
				tabIndex={clickable ? 0 : undefined}
				onKeyDown={clickable ? handleKeyDown : undefined}
			>
				<Box sx={sx.imageClip}>
					<Box
						component='img'
						src={attachment.previewUrl}
						alt={attachment.name}
						sx={sx.image}
					/>

					{isUploading && (
						<Box sx={sx.uploadingOverlay}>
							<CircularProgress
								variant='determinate'
								value={attachment.progress}
								size={32}
								thickness={4}
							/>
						</Box>
					)}

					{hasError && (
						<Box sx={sx.errorOverlay}>
							<ErrorOutlineIcon fontSize='medium' />
						</Box>
					)}
				</Box>

				{onRemove && (
					<IconButton
						size='small'
						sx={sx.removeButton}
						onClick={handleRemove}
						aria-label={t('Remove attachment')}
					>
						<CloseIcon />
					</IconButton>
				)}
			</Box>
		</Tooltip>
	);
};
